import React, { useEffect, useState } from 'react';
import { fetchGoogleSheetData } from '../api/fetchGoogleSheetData';
import IncentiveTable from '../components/IncentiveTable';
import Loading from '../components/Loading';

import '../stylesheets/query_table/Incentives.scss';

// Define agentMapping at a level where it will be visible to all functions
const agentMapping = {
  AKR: 'AKR',
  MBM: 'Manisha',
  KKS: 'Swedha Karnani',
  RAM: 'Ram Pandey',
  IRF: 'Irfan',
  SRI: 'Srishti Verma',
  TEZ: 'Sneha',
  ANC: 'Anchal Verma',
  MOH: 'Mohit Vats',
  HAR: 'Harshit Mishra',
  THI: 'Sanjay',
  RKS: 'Shivani',
  APU: 'Apurva',
  SAR: 'Sajal',
  KCV: 'Sonam',
  RKT: 'Ravi',
  NIK: 'Nikhil',
  PUR: 'Purusharth',
  APS: 'Abhay',
  AMI: 'Amit',
  ANK: 'Ankit Singh',
  TAN: 'Tanu',
  JEE: 'Jeetu',
  SHI: 'Shiv',
  GAR: 'Gargi',
  DEE: 'Deekshika',
  ADI: 'Aditi',
  SNK: 'Shasquat',
};

const parseDate = (dateString) => {
  if (!dateString) return null;
  const parsedDate = new Date(dateString);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export default function Incentives() {
  const [isLoading, setIsLoading] = useState(true);
  const [incentivesData, setIncentivesData] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [selectedCourse, setSelectedCourse] = useState('All'); // New state for course selection
  const [agents, setAgents] = useState([]);
  const [months, setMonths] = useState([]);
  const [courses, setCourses] = useState([]); // New state to store courses

  useEffect(() => {
    fetchAndProcessData();
  }, []);

  const fetchAndProcessData = async () => {
    try {
      const rawData = await fetchGoogleSheetData();
      console.log('Raw Data:', rawData); // Debugging: log raw data
      const processedData = processRawData(rawData);
      setIncentivesData(processedData.processedIncentives);
      setAgents(['All', ...processedData.agents]);
      setMonths(['All', ...processedData.months]);
      setCourses(['All', ...processedData.courses]); // Set courses for dropdown
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const processRawData = (data) => {
    let agentMap = {};
    let agentSet = new Set();
    let monthSet = new Set();
    let courseSet = new Set(); // Set for unique courses

    data.forEach(({ couponCode, TransDate, courseName, amount_paid }) => {
      if (!couponCode || couponCode.trim() === '') return;
      const parsedDate = parseDate(TransDate);
      if (!parsedDate) return; // Continue if date is invalid

      const month = parsedDate.toLocaleString('default', { month: 'long', year: 'numeric' });
      const agentName = agentMapping[couponCode.substring(0, 3)] || 'Unknown';

      if (!agentMap[agentName]) agentMap[agentName] = {};
      if (!agentMap[agentName][month]) agentMap[agentName][month] = {};
      if (!agentMap[agentName][month][courseName]) {
        agentMap[agentName][month][courseName] = { count: 0, totalRevenue: 0 };
      }

      agentMap[agentName][month][courseName].count++;
      agentMap[agentName][month][courseName].totalRevenue += parseFloat(amount_paid);
      agentSet.add(agentName);
      monthSet.add(month);
      courseSet.add(courseName); // Add course name to course set
    });

    console.log('Processed Data:', agentMap); // Debugging: log processed data
    return {
      processedIncentives: flattenData(agentMap),
      agents: Array.from(agentSet),
      months: Array.from(monthSet),
      courses: Array.from(courseSet), // Return courses
    };
  };

  const flattenData = (agentMap) => {
    let result = [];
    Object.entries(agentMap).forEach(([agent, monthData]) => {
      Object.entries(monthData).forEach(([month, courses]) => {
        Object.entries(courses).forEach(([courseName, { count, totalRevenue }]) => {
          result.push({ agentName: agent, courseName, month, count, totalRevenue });
        });
      });
    });
    return result;
  };

  if (isLoading) return <Loading />;

  const filteredData = incentivesData.filter(({ agentName, month, courseName }) =>
    (selectedAgent === 'All' || agentName === selectedAgent) &&
    (selectedMonth === 'All' || month === selectedMonth) &&
    (selectedCourse === 'All' || courseName === selectedCourse) // Filter by selected course
  );

  // Calculate total courses and revenue after filtering
  const totalCourses = filteredData.reduce((acc, item) => acc + item.count, 0);
  const totalRevenue = filteredData.reduce((acc, item) => acc + item.totalRevenue, 0);

  return (
    <div className="container">
      <h1 className="header">Incentives Summary</h1>
      <div className="filter-container">
        <div className="filter-group">
          <label htmlFor="agentDropdown">Agent Name</label>
          <select id="agentDropdown" value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)}>
            {agents.map(agent => <option key={agent} value={agent}>{agent}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="monthDropdown">Month</label>
          <select id="monthDropdown" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            {months.map(month => <option key={month} value={month}>{month}</option>)}
          </select>
        </div>
        <div className="filter-group"> {/* New dropdown for selecting course */}
          <label htmlFor="courseDropdown">Course</label>
          <select id="courseDropdown" value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)}>
            {courses.map(course => <option key={course} value={course}>{course}</option>)}
          </select>
        </div>
      </div>
      <div className="summary">
        <p>Total Courses: {totalCourses}</p>
        <p>Total Revenue: {totalRevenue.toLocaleString('en-US', { style: 'currency', currency: 'INR' })}</p>
      </div>
      {filteredData.length > 0 ? (
        <IncentiveTable data={filteredData} columns={columns} />
      ) : (
        <p>No data available for the selected filters.</p>
      )}
    </div>
  );
}

const columns = [
  { header: 'Agent Name', accessor: 'agentName' },
  { header: 'Course Name', accessor: 'courseName' },
  { header: 'Count', accessor: 'count' },
  { header: 'Total Revenue', accessor: 'totalRevenue' }
];
